import { Component, Inject, inject, OnInit, Optional, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { forkJoin, map, of, catchError, BehaviorSubject, combineLatest, timer, tap, switchMap, shareReplay, filter, startWith, distinctUntilChanged, takeUntil, merge, mapTo, debounceTime } from 'rxjs';
import { DynamicFormsV2Component } from '../../shared/dynamic-forms-v2/dynamic-forms-v2.component';
import { formMetaData, FormFields } from '../../shared/dynamic-forms.interface2';
import { FormFieldsSelectDataService } from '../../shared/form-fields-select-data.service';
import { MedicationRequest, CodeableReference, MedicationDispense } from 'fhir/r5';
import { CommonModule } from '@angular/common';
import { HttpClient, HttpContext } from '@angular/common/http';
import { backendEndPointToken } from '../../app.config';
import { UtilityService } from '../../shared/utility.service';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DisableCountParam, DisableSortParam, LoadingUIEnabled } from '../../loading.interceptor';
import { EmphasisDirective } from '../../shared/directives/emphasis.directive';

@Component({
  selector: 'app-add-medication-administration',
  standalone: true,
  imports: [
    CommonModule, 
    DynamicFormsV2Component, 
    ReactiveFormsModule, 
    MatAutocompleteModule, 
    MatInputModule, 
    MatFormFieldModule, 
    MatIconModule, 
    MatProgressSpinnerModule,
    EmphasisDirective
  ],
  templateUrl: './add-medication-administration.component.html',
  styleUrl: './add-medication-administration.component.scss'
})
export class AddMedicationAdministrationComponent implements OnInit {
  formFieldsDataService = inject(FormFieldsSelectDataService);
  http = inject(HttpClient);
  backendEndPoint = inject(backendEndPointToken);
  utilityService = inject(UtilityService);

  @ViewChild('cref') dynamicForms?: DynamicFormsV2Component;

  formMetaData!: formMetaData;
  formFields!: FormFields[];

  // ---- RxNorm medication search ----
  rxNormMedicationCtrl = new FormControl<any>('' as any);
  rxNormSearching$ = new BehaviorSubject<boolean>(false);
  private rxNormTerm$ = this.rxNormMedicationCtrl.valueChanges.pipe(
    startWith(''),
    map(v => (typeof v === 'string' ? v : v?.label?.display ?? v?.display ?? '').toString().trim()),
    debounceTime(250),
    distinctUntilChanged()
  );

  private rxNormCachedOptions$ = new BehaviorSubject<Array<{ code: string; display: string; system: string; ingredients: string[] }>>([]);
  private stockMedicationOptions$ = new BehaviorSubject<Array<{ reference: string; display: string; inStock: boolean; ingredients: string; kind: 'stock' }>>([]);

  rxNormMergedMedicationOptions$ = combineLatest([
    this.rxNormTerm$,
    this.stockMedicationOptions$.asObservable()
  ]).pipe(
    switchMap(([term, stock]: [string, any[]]) => {
      const cleaned = term.trim();
      if (!cleaned) {
        this.rxNormSearching$.next(false);
        return of({ term: '', stock, rx: this.rxNormCachedOptions$.getValue() || [] });
      }

      const prev = this.rxNormCachedOptions$.getValue() || [];
      const hasMatch = prev.some(p => p.display.toLowerCase().includes(cleaned.toLowerCase()));
      if (hasMatch) {
        this.rxNormSearching$.next(false);
        return of({ term: cleaned, stock, rx: this.rxNormCachedOptions$.getValue() || [] });
      }

      return timer(0, 1000).pipe(
        tap(() => this.rxNormSearching$.next(true)),
        switchMap(() => this.searchRxNormSBD(cleaned).pipe(
          catchError(() => this.searchRxNormSCD(cleaned)),
          catchError(() => of([]))
        )),
        map(() => ({ term: cleaned, stock, rx: this.rxNormCachedOptions$.getValue() || [] })),
        tap(() => { this.rxNormSearching$.next(false); })
      );
    }),
    map(({ term, stock, rx }) => {
      const cleaned = term.trim().toLowerCase();
      
      const stockMapped = stock.map(s => ({
        kind: 'stock',
        reference: s.reference,
        label: { display: s.display, ingredients: s.ingredients },
        inStock: s.inStock
      }));

      const rxMapped = rx.map((r: any) => ({
        kind: 'rxnorm',
        code: r.code,
        system: r.system,
        label: { display: r.display, ingredients: r.ingredients.join(', ') }
      }));

      const all = [...stockMapped, ...rxMapped].filter(x => {
        if (!cleaned) return true;
        return x.label.display.toLowerCase().includes(cleaned);
      });

      const unique = Array.from(new Map(all.map(item => [item.label.display, item])).values());

      if (cleaned && !unique.some(x => x.label.display.toLowerCase() === cleaned)) {
        unique.push({
          kind: 'userExactWords',
          label: { display: term, ingredients: '' }
        } as any);
      }

      return unique;
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  loading$ = this.rxNormTerm$.pipe(
    switchMap(() =>
      this.rxNormSearching$.pipe(
        switchMap(inFlight => {
          if (!inFlight) {
            return of(false);
          }
          return timer(250).pipe(
            mapTo(true),
            takeUntil(
              merge(
                this.rxNormTerm$,
                this.rxNormSearching$.pipe(filter(active => !active))
              )
            )
          );
        })
      )
    ),
    startWith(false)
  );

  displayRxNormOption = (opt: any) => {
    if (typeof opt === 'string') return opt;
    return opt?.label?.display ?? opt?.display ?? '';
  };

  private isRxNormPackOrKit(display: string): boolean {
    const s = (display ?? '').toString();
    return s.includes('{') || /\bPack\b/i.test(s);
  }

  private simplifyRxNormDisplay(raw: string): string {
    const s = (raw ?? '').toString().trim();
    if (!s) return '';
    const brandMatch = s.match(/\[([^\]]+)\]/);
    if (brandMatch?.[1]?.trim()) {
      return brandMatch[1].trim();
    } else {
      return '';
    }
  }

  private searchRxNormSBD(term: string) {
    const url = `https://rxnav.nlm.nih.gov/REST/drugs.json?type=SBD&name=${encodeURIComponent(term)}`;
    const context = new HttpContext().set(LoadingUIEnabled, false).set(DisableCountParam, true).set(DisableSortParam, true);
    this.rxNormCachedOptions$.next([]);
    
    return this.http.get<any>(url, { context }).pipe(
      map((res: any) => {
        const system = 'http://www.nlm.nih.gov/research/umls/rxnorm';
        const groups = res?.drugGroup?.conceptGroup;
        const list = Array.isArray(groups) ? groups : (groups ? [groups] : []);

        const out: Array<{ code: string; display: string; system: string; ingredients: string[] }> = [];

        for (const g of list) {
          const tty = (g?.tty ?? '').toString().toUpperCase();
          if (tty && tty !== 'SBD' && tty !== 'SCD') continue;

          const props = Array.isArray(g?.conceptProperties) ? g.conceptProperties : [];
          for (const cp of props) {
            const code = (cp?.rxcui ?? '').toString().trim();
            const rawDisplay = (cp?.name ?? '').toString().trim();
            if (!code || !rawDisplay) continue;
            if (this.isRxNormPackOrKit(rawDisplay)) continue;

            const brandDisplay = this.simplifyRxNormDisplay(rawDisplay);
            const syn = (cp?.synonym ?? '').toString().trim();
            const display = brandDisplay || syn || rawDisplay;
            
            out.push({ code, display, system, ingredients: [] });
          }
        }
        return out;
      }),
      switchMap(baseList => {
        if (!baseList.length) return of([]);
        const withIngredients$ = baseList.slice(0, 10).map(item => this.fetchIngredientsForRxCUI(item.code, item));
        return forkJoin(withIngredients$);
      }),
      tap(results => {
        const merged = [...(this.rxNormCachedOptions$.getValue() || []), ...results];
        const unique = Array.from(new Map(merged.map(item => [item.code, item])).values());
        this.rxNormCachedOptions$.next(unique);
      })
    );
  }

  private searchRxNormSCD(term: string) {
    const url = `https://rxnav.nlm.nih.gov/REST/drugs.json?name=${encodeURIComponent(term)}`;
    const context = new HttpContext().set(LoadingUIEnabled, false).set(DisableCountParam, true).set(DisableSortParam, true);
    this.rxNormCachedOptions$.next([]);

    return this.http.get<any>(url, { context }).pipe(
      map((res: any) => {
        const system = 'http://www.nlm.nih.gov/research/umls/rxnorm';
        const groups = res?.drugGroup?.conceptGroup;
        const list = Array.isArray(groups) ? groups : (groups ? [groups] : []);
        const out: Array<{ code: string; display: string; system: string; ingredients: string[] }> = [];

        for (const g of list) {
          const tty = (g?.tty ?? '').toString().toUpperCase();
          if (tty && tty !== 'SBD' && tty !== 'SCD') continue;

          const props = Array.isArray(g?.conceptProperties) ? g.conceptProperties : [];
          for (const cp of props) {
            const code = (cp?.rxcui ?? '').toString().trim();
            const rawDisplay = (cp?.name ?? '').toString().trim();
            if (!code || !rawDisplay) continue;
            if (this.isRxNormPackOrKit(rawDisplay)) continue;

            out.push({ code, display: rawDisplay, system, ingredients: [] });
          }
        }
        return out;
      }),
      switchMap(baseList => {
        if (!baseList.length) return of([]);
        const withIngredients$ = baseList.slice(0, 10).map(item => this.fetchIngredientsForRxCUI(item.code, item));
        return forkJoin(withIngredients$);
      }),
      tap(results => {
        const merged = [...(this.rxNormCachedOptions$.getValue() || []), ...results];
        const unique = Array.from(new Map(merged.map(item => [item.code, item])).values());
        this.rxNormCachedOptions$.next(unique);
      })
    );
  }

  private fetchIngredientsForRxCUI(rxcui: string, baseItem: any) {
    const url = `https://rxnav.nlm.nih.gov/REST/rxcui/${rxcui}/related.json?tty=IN`;
    const context = new HttpContext().set(LoadingUIEnabled, false).set(DisableCountParam, true).set(DisableSortParam, true);
    
    return this.http.get<any>(url, { context }).pipe(
      map(res => {
        const groups = res?.relatedGroup?.conceptGroup ?? [];
        let ingredients: string[] = [];
        for (const g of groups) {
          if (g.tty === 'IN' && Array.isArray(g.conceptProperties)) {
            ingredients = g.conceptProperties.map((cp: any) => cp.name);
          }
        }
        return { ...baseItem, ingredients };
      }),
      catchError(() => of({ ...baseItem, ingredients: [] }))
    );
  }

  constructor(
    @Optional() public dialogRef: MatDialogRef<AddMedicationAdministrationComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: { medicationRequest: MedicationRequest, medicationDispense?: MedicationDispense }
  ) {}

  ngOnInit() {
    this.formMetaData = {
      formName: 'Medication Administration',
      formDescription: 'Use this form to document the actual administration of a medication to a patient.',
      submitText: 'Confirm Administration'
    };

    const medReq = this.data?.medicationRequest;
    
    // Fetch MedicationDispense if not provided
    const medDisp$ = this.data?.medicationDispense 
      ? of({ entry: [{ resource: this.data.medicationDispense }] })
      : medReq?.id 
        ? this.http.get<any>(`${this.backendEndPoint}/MedicationDispense?authorizingPrescription=MedicationRequest/${medReq.id}&_sort=-_lastUpdated`).pipe(catchError(() => of({ entry: [] })))
        : of({ entry: [] });

    const medReqDisplay = this.getMedicationDisplay(medReq);

    forkJoin({
      medDispenseResponse: medDisp$,
      MedInStock: this.http.get<any>(`${this.backendEndPoint}/Medication?_count=1000`).pipe(catchError(() => of({ entry: [] })))
    }).subscribe({
      next: (g: any) => {
        const dispenseResource = g.medDispenseResponse?.entry?.[0]?.resource;
        const medDispDisplay = this.getMedicationDisplay(dispenseResource);

        let stockOptions: any[] = [];

        // Add Prescribed Option
        if (medReqDisplay) {
          stockOptions.push({
            kind: 'stock',
            reference: (medReq?.medication as CodeableReference)?.reference?.reference || '',
            display: `[Prescribed] ${medReqDisplay}`,
            inStock: true,
            ingredients: ''
          });
        }
        
        // Add Dispensed Option
        if (medDispDisplay && medDispDisplay !== medReqDisplay) {
          stockOptions.push({
            kind: 'stock',
            reference: (dispenseResource?.medication as CodeableReference)?.reference?.reference || '',
            display: `[Dispensed] ${medDispDisplay}`,
            inStock: true,
            ingredients: ''
          });
        }

        // Add available stock
        if (g.MedInStock && g.MedInStock.entry) {
          const stockMeds = this.utilityService.filterAvailableMedications(g.MedInStock.entry).map((e: any) => ({
            kind: 'stock',
            reference: 'Medication/' + e.resource.id,
            display: `${e.resource.code?.text || e.resource.code?.coding?.[0]?.display}`,
            inStock: true,
            ingredients: e.resource.ingredient?.map((i: any) => i.itemCodeableConcept?.text).join(', ') || ''
          }));
          stockOptions = [...stockOptions, ...stockMeds];
        }
        
        this.stockMedicationOptions$.next(stockOptions);
        
        // Pre-select the best option
        if (medDispDisplay) {
          this.rxNormMedicationCtrl.setValue(stockOptions.find(o => o.display.includes('[Dispensed]')));
        } else if (medReqDisplay) {
          this.rxNormMedicationCtrl.setValue(stockOptions.find(o => o.display.includes('[Prescribed]')));
        }

        const defaultDoseValue = medReq?.dosageInstruction?.[0]?.doseAndRate?.[0]?.doseQuantity?.value?.toString() || '';
        const defaultDoseUnit = medReq?.dosageInstruction?.[0]?.doseAndRate?.[0]?.doseQuantity?.unit || '';
        const defaultDose = `${defaultDoseValue} ${defaultDoseUnit}`.trim();

        const defaultRoute = medReq?.dosageInstruction?.[0]?.route?.coding?.[0]?.display || medReq?.dosageInstruction?.[0]?.route?.text || '';
        const defaultSite = medReq?.dosageInstruction?.[0]?.site?.coding?.[0]?.display || medReq?.dosageInstruction?.[0]?.site?.text || '';

        this.formFields = [
          {
            generalProperties: {
              fieldApiName: 'status',
              fieldName: 'Status',
              fieldLabel: 'Status',
              auth: { read: 'all', write: 'doctor, nurse' },
              validations: [{ type: 'default', name: 'required' }],
              fieldType: 'SingleCodeField',
              isArray: false,
              isGroup: false,
              value: 'completed'
            },
            data: ['in-progress', 'not-done', 'on-hold', 'completed', 'entered-in-error', 'stopped', 'unknown']
          },
          {
            generalProperties: {
              fieldApiName: 'effectiveDateTime',
              fieldName: 'Administration Time',
              fieldLabel: 'Administration Time',
              auth: { read: 'all', write: 'doctor, nurse' },
              validations: [{ type: 'default', name: 'required' }],
              inputType: 'datetime-local',
              isArray: false,
              isGroup: false,
              value: new Date().toISOString()
            }
          },
          {
            generalProperties: {
              fieldApiName: 'dosage',
              fieldName: 'Quantity Given',
              fieldLabel: 'Quantity Given',
              auth: { read: 'all', write: 'doctor, nurse' },
              inputType: 'text',
              isArray: false,
              isGroup: false,
              value: defaultDose
            }
          },
          {
            generalProperties: {
              fieldApiName: 'route',
              fieldName: 'Route',
              fieldLabel: 'Route',
              auth: { read: 'all', write: 'doctor, nurse' },
              inputType: 'text',
              isArray: false,
              isGroup: false,
              value: defaultRoute
            }
          },
          {
            generalProperties: {
              fieldApiName: 'site',
              fieldName: 'Site',
              fieldLabel: 'Site',
              auth: { read: 'all', write: 'doctor, nurse' },
              inputType: 'text',
              isArray: false,
              isGroup: false,
              value: defaultSite
            }
          },
          {
            generalProperties: {
              fieldApiName: 'note',
              fieldName: 'Administration Notes',
              fieldLabel: 'Administration Notes',
              auth: { read: 'all', write: 'doctor, nurse' },
              inputType: 'textarea',
              isArray: false,
              isGroup: false
            }
          }
        ] as any[];
      }
    });
  }

  private getMedicationDisplay(resource: any): string {
    if (!resource || !resource.medication) return '';
    if ((resource.medication as CodeableReference).concept?.coding?.[0]) {
      return (resource.medication as CodeableReference).concept!.coding![0].display || '';
    } else if ((resource.medication as CodeableReference).reference) {
      return (resource.medication as CodeableReference).reference?.display || (resource.medication as CodeableReference).reference?.reference || '';
    } else if ((resource.medication as any).text) {
      return (resource.medication as any).text;
    }
    return '';
  }

  parseQuantity(doseStr: string, defaultUnit?: string): any {
    if (!doseStr) return undefined;
    const trimmed = doseStr.trim();
    const match = trimmed.match(/^([0-9]+(?:\.[0-9]+)?)\s*(.*)$/);
    if (match) {
      const val = parseFloat(match[1]);
      const unit = match[2] || defaultUnit || '';
      return {
        value: val,
        unit: unit,
        system: 'http://unitsofmeasure.org',
        code: unit
      };
    }
    return undefined;
  }

  processValues(values: any) {
    if (this.dialogRef) {
      const rxVal = this.rxNormMedicationCtrl.value;
      
      let medicationObj: any = {};
      
      if (rxVal?.kind === 'stock' && rxVal.reference) {
        medicationObj = { medicationReference: { reference: rxVal.reference } };
      } else if (rxVal?.kind === 'rxnorm') {
        medicationObj = { medicationCodeableConcept: { coding: [{ code: rxVal.code, system: rxVal.system, display: rxVal.label.display }] } };
      } else if (rxVal?.kind === 'userExactWords') {
        medicationObj = { medicationCodeableConcept: { text: rxVal.label.display } };
      } else if (typeof rxVal === 'string') {
        medicationObj = { medicationCodeableConcept: { text: rxVal } };
      }

      const medReq = this.data?.medicationRequest;
      const defaultDoseUnit = medReq?.dosageInstruction?.[0]?.doseAndRate?.[0]?.doseQuantity?.unit || '';
      
      const parsedDose = this.parseQuantity(values.dosage, defaultDoseUnit);

      // Get route CodeableConcept
      let routeObj: any = undefined;
      if (values.route) {
        const origRoute = medReq?.dosageInstruction?.[0]?.route;
        const origRouteDisplay = origRoute?.coding?.[0]?.display || origRoute?.text || '';
        if (values.route === origRouteDisplay && origRoute) {
          routeObj = origRoute;
        } else {
          routeObj = { text: values.route };
        }
      }

      // Get site CodeableConcept
      let siteObj: any = undefined;
      if (values.site) {
        const origSite = medReq?.dosageInstruction?.[0]?.site;
        const origSiteDisplay = origSite?.coding?.[0]?.display || origSite?.text || '';
        if (values.site === origSiteDisplay && origSite) {
          siteObj = origSite;
        } else {
          siteObj = { text: values.site };
        }
      }

      const dosageObj: any = {
        text: values.dosage
      };
      if (parsedDose) {
        dosageObj.dose = parsedDose;
      }
      if (routeObj) {
        dosageObj.route = routeObj;
      }
      if (siteObj) {
        dosageObj.site = siteObj;
      }

      const resource = {
        resourceType: 'MedicationAdministration',
        status: values.status,
        ...medicationObj,
        subject: this.data?.medicationRequest?.subject,
        context: this.data?.medicationRequest?.encounter,
        effectiveDateTime: values.effectiveDateTime,
        dosage: dosageObj,
        note: values.note ? [{ text: values.note }] : undefined,
        request: {
          reference: this.data?.medicationRequest?.id ? `MedicationRequest/${this.data.medicationRequest.id}` : undefined
        }
      };

      this.dialogRef.close(resource);
    }
  }
}
